/**
 * EditToolbar — Unified edit-mode toolbar, shared between Graph View and
 * List View.
 *
 * Mounted inside the recipe header strip's toolbar slot (below its title
 * row, inside the same bordered box — a thin top rule separates the two),
 * visible only while the store is in edit mode. Decouples edit actions
 * from any particular view:
 *   - Left (node actions, in order): [+] create — a plain node with
 *     nothing selected, or a child of the selection with 1+ nodes
 *     selected (the icon swaps to ↧ then, the mirror of ↥, to signal
 *     "child" rather than "new/unattached"); [↥] add parent (insert a new
 *     shared parent above the selection, no rewiring of existing links);
 *     [↟] transform (insert a new shared parent above the selection *and*
 *     take over every parent link the selection currently has — the ↟'s
 *     doubled head marks it as the heavier of the two "add a parent"
 *     operations); [✎] edit selected node; [★] make selected node the
 *     main root (✎/★ only apply to exactly one node — shown/enabled
 *     then); [×] delete whatever's selected (shown with 1+ selected —
 *     same two-click arm/confirm as the per-node × buttons elsewhere, and
 *     the same effect with exactly one node selected).
 *   - Right: [⛶] toggle multi-select mode — kept apart from the node
 *     actions since it's a mode switch, not an operation on nodes.
 * (Save/Discard live in the recipe header strip instead, next to the
 * edit-mode toggle — decoupled from this per-node/multi-select toolbar.)
 *
 * [ⓘ] (info), [⚘] (go to ingredient), and [⚗]/[⚗ׯ] (add/modify/remove
 * elaboration) used to live here too — the one exception to "edit-mode
 * only", since viewing/adjusting them isn't itself an edit-tracked
 * change. They've since moved to List View's own per-row header (see
 * ListNodeItem.ts), so this toolbar is purely edit-mode-gated again —
 * Graph View no longer has a direct way to reach them.
 *
 * With 2+ nodes selected, [+] creates a node as the child of all of them,
 * [↥]/[↟] add a parent to/transform all of them (a new shared parent),
 * and [×] deletes all of them — [✎]/[★] don't apply to more than one
 * node and are hidden then (GraphVisualizer's job to enforce; this class
 * just exposes the visibility setters — a button that doesn't apply is
 * never rendered dimmed, only hidden).
 *
 * In "global" edition mode (no recipe focused — see RecipeStore's
 * focus/select distinction), [+] creates a new recipe regardless of any
 * recipe/node that happens to be selected while browsing unfocused, and
 * a [×] delete-recipe button sits right next to it — shown only then,
 * and only with a recipe selected, to delete that recipe (single click;
 * GraphVisualizer's handleDeleteRecipe() shows its own confirmation
 * dialog, so no arm/confirm here). Every other button is hidden until a
 * recipe is focused (also GraphVisualizer's job to enforce).
 *
 * Icons are plain Unicode glyphs (no emoji, no icon font) per design
 * decision — may be swapped for an icon font or SVG glyphs later.
 */

const ICON_CREATE = '+';
const ICON_CREATE_CHILD = '↧'; // downwards arrow from bar — mirrors ↥ (add parent)
const ICON_MAKE_MAIN_ROOT = '★';
const ICON_EDIT = '✎';
const ICON_ADD_PARENT = '↥'; // upwards arrow from bar — mirrors ↧ (add child): plain, no rewiring
const ICON_TRANSFORM = '↟'; // upwards two-headed arrow — add-parent's heavier sibling: also takes over existing links
const ICON_MULTI_SELECT = '⛶';
const ICON_DELETE_ALL = '×';
const ICON_DELETE_RECIPE = '×';

export interface EditToolbarCallbacks {
  onCreate: () => void;
  onMakeMainRoot: () => void;
  onEdit: () => void;
  onAddParent: () => void;
  onTransform: () => void;
  onToggleMultiSelect: () => void;
  onDeleteAllConfirmed: () => void;
  onDeleteRecipe: () => void;
}

export class EditToolbar {
  private element: HTMLElement | null = null;
  private createBtn: HTMLButtonElement | null = null;
  private deleteRecipeBtn: HTMLButtonElement | null = null;
  private makeMainRootBtn: HTMLButtonElement | null = null;
  private editBtn: HTMLButtonElement | null = null;
  private addParentBtn: HTMLButtonElement | null = null;
  private transformBtn: HTMLButtonElement | null = null;
  private multiSelectBtn: HTMLButtonElement | null = null;
  private deleteAllBtn: HTMLButtonElement | null = null;
  private deleteAllArmed: boolean = false;
  private deleteAllOutsideClickHandler: ((e: MouseEvent) => void) | null = null;

  // [✎] has two independent conditions to be shown at all — applicable to
  // the current selection, and allowed on this screen size (hidden on
  // wide, where the side panel already shows the same editable tabs
  // inline) — tracked separately so whichever setter fires alone still
  // combines correctly with whatever the other last said.
  private editApplicable: boolean = false;
  private editAllowedByScreen: boolean = true;

  render(container: HTMLElement, callbacks: EditToolbarCallbacks): void {
    const existing = container.querySelector('.edit-toolbar');
    if (existing) existing.remove();

    this.element = document.createElement('div');
    this.element.className = 'edit-toolbar';
    this.element.style.cssText = `
      display: none;
      justify-content: space-between;
      align-items: center;
      border-top: 1px solid var(--accent-alpha-20);
      margin-top: 8px;
      padding-top: 8px;
      gap: 8px;
      font-family: 'Courier New', Consolas, 'Liberation Mono', monospace;
    `;

    // Left: node actions, in order.
    const actionsGroup = document.createElement('div');
    actionsGroup.className = 'edit-toolbar-actions';
    actionsGroup.style.cssText = 'display: flex; align-items: center; gap: 6px;';

    this.createBtn = this.makeButton(ICON_CREATE, 'Crea (recepta o node)', () => callbacks.onCreate());
    actionsGroup.appendChild(this.createBtn);

    // Delete recipe — sits right next to [+], since in "global" edition
    // mode (no recipe focused) those two are the only actions that ever
    // apply: create a new recipe, or delete the one currently selected.
    // Single click, no arm/confirm — handleDeleteRecipe() (GraphVisualizer)
    // already shows its own confirmation dialog.
    this.deleteRecipeBtn = this.makeButton(ICON_DELETE_RECIPE, 'Elimina la recepta seleccionada', () => callbacks.onDeleteRecipe());
    this.deleteRecipeBtn.style.display = 'none';
    actionsGroup.appendChild(this.deleteRecipeBtn);

    this.addParentBtn = this.makeButton(ICON_ADD_PARENT, 'Afegeix un node pare', () => callbacks.onAddParent());
    this.addParentBtn.style.display = 'none';
    actionsGroup.appendChild(this.addParentBtn);

    this.transformBtn = this.makeButton(ICON_TRANSFORM, 'Transforma el node seleccionat', () => callbacks.onTransform());
    this.transformBtn.style.display = 'none';
    actionsGroup.appendChild(this.transformBtn);

    this.editBtn = this.makeButton(ICON_EDIT, 'Edita el node seleccionat', () => callbacks.onEdit());
    this.editBtn.style.display = 'none';
    actionsGroup.appendChild(this.editBtn);

    this.makeMainRootBtn = this.makeButton(ICON_MAKE_MAIN_ROOT, 'Fes node principal', () => callbacks.onMakeMainRoot());
    this.makeMainRootBtn.style.display = 'none';
    actionsGroup.appendChild(this.makeMainRootBtn);

    // Delete — shown with 1+ nodes selected (same effect as the per-node
    // × button when there's just one). Two-click arm/confirm, same
    // pattern used everywhere else (ImageUploader, List View, Graph
    // View's own per-node × button).
    this.deleteAllBtn = this.makeButton(ICON_DELETE_ALL, 'Esborra els nodes seleccionats', () => {
      if (this.deleteAllArmed) {
        this.disarmDeleteAll();
        callbacks.onDeleteAllConfirmed();
      } else {
        this.armDeleteAll();
      }
    });
    this.deleteAllBtn.style.display = 'none';
    actionsGroup.appendChild(this.deleteAllBtn);

    this.element.appendChild(actionsGroup);

    // Right: multi-select mode toggle — a mode switch, not a node
    // operation, so it's kept apart from the action buttons.
    const modeGroup = document.createElement('div');
    modeGroup.className = 'edit-toolbar-mode';
    modeGroup.style.cssText = 'display: flex; align-items: center; gap: 6px;';

    this.multiSelectBtn = this.makeButton(ICON_MULTI_SELECT, 'Selecció múltiple', () => callbacks.onToggleMultiSelect());
    modeGroup.appendChild(this.multiSelectBtn);

    this.element.appendChild(modeGroup);

    container.appendChild(this.element);
  }

  private makeButton(icon: string, title: string, onClick: () => void, accent: boolean = false): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.title = title;
    btn.textContent = icon;
    btn.style.cssText = `
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      font-weight: bold;
      font-family: inherit;
      border-radius: 50%;
      border: 1px solid var(--accent-alpha-40);
      background: ${accent ? 'var(--accent)' : 'transparent'};
      color: ${accent ? '#0d1117' : 'var(--accent)'};
      cursor: pointer;
      transition: all 0.15s ease;
      flex-shrink: 0;
    `;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (btn.disabled) return;
      onClick();
    });
    return btn;
  }

  show(): void {
    if (this.element) this.element.style.display = 'flex';
  }

  hide(): void {
    if (this.element) this.element.style.display = 'none';
  }

  /**
   * Show/hide the [✎] edit button — applicable only to exactly one
   * selected node. Combines with setEditButtonVisible()'s screen-size
   * condition (both must hold for the button to actually show); each
   * setter can be called independently and still combines correctly.
   */
  setEditEnabled(enabled: boolean): void {
    this.editApplicable = enabled;
    this.updateEditButtonDisplay();
  }

  /**
   * Show/hide the [+] create button — edit-mode only. Unlike every other
   * button here this used to rely on the whole toolbar being hidden
   * outside edit mode; now that the bar itself also shows for the
   * elaboration buttons in normal mode, this needs its own condition.
   */
  setCreateVisible(visible: boolean): void {
    if (this.createBtn) this.createBtn.style.display = visible ? 'flex' : 'none';
  }

  /**
   * Swap the [+] button's icon to ↧ (mirroring transform's ↥) whenever
   * there's a multi-selection (2+) to attach the new node to as a shared
   * child — signals "this creates a child" rather than a plain,
   * unattached node. A *single* selected node doesn't trigger this
   * anymore — that row has its own [↧] now — so [+] here always reads as
   * "add a new node", independent of any single selection.
   */
  setCreateAsChildMode(isChild: boolean): void {
    if (!this.createBtn) return;
    this.createBtn.textContent = isChild ? ICON_CREATE_CHILD : ICON_CREATE;
    this.createBtn.title = isChild ? 'Crea un node fill de la selecció' : 'Crea (recepta o node)';
  }

  /**
   * Show/hide the [×] delete-recipe button — shown only in "global"
   * edition mode (no recipe focused) with a recipe selected.
   */
  setDeleteRecipeVisible(visible: boolean): void {
    if (this.deleteRecipeBtn) this.deleteRecipeBtn.style.display = visible ? 'flex' : 'none';
  }

  /**
   * Show/hide the [↥] add-parent button — multi-select (2+) only now; a
   * single selected node has its own [↥] on its row in List View instead
   * (ListNodeItem.ts). Graph View has no equivalent for a single node —
   * this toolbar was its only way to reach the action, and still is for
   * 2+.
   */
  setAddParentEnabled(enabled: boolean): void {
    if (this.addParentBtn) this.addParentBtn.style.display = enabled ? 'flex' : 'none';
  }

  /**
   * Show/hide the [↟] transform button — same story as
   * setAddParentEnabled() above: multi-select (2+) only now, List View's
   * own row covers the single-node case, Graph View has nothing else for
   * it.
   */
  setTransformEnabled(enabled: boolean): void {
    if (this.transformBtn) this.transformBtn.style.display = enabled ? 'flex' : 'none';
  }

  /**
   * Show/hide the [⛶] multi-select toggle — hidden in "global" edition
   * mode (no recipe focused), since multi-select only applies to nodes
   * within a focused recipe.
   */
  setMultiSelectVisible(visible: boolean): void {
    if (this.multiSelectBtn) this.multiSelectBtn.style.display = visible ? 'flex' : 'none';
  }

  /** Reflect whether multi-select mode is currently on (pressed/filled vs. normal). */
  setMultiSelectActive(active: boolean): void {
    if (!this.multiSelectBtn) return;
    this.multiSelectBtn.style.background = active ? 'var(--accent)' : 'transparent';
    this.multiSelectBtn.style.color = active ? '#0d1117' : 'var(--accent)';
    this.multiSelectBtn.style.borderColor = active ? 'var(--accent)' : 'var(--accent-alpha-40)';
  }

  /** Show/hide the [×] delete button — shown whenever 1+ nodes are selected. */
  setDeleteAllVisible(visible: boolean): void {
    if (this.deleteAllBtn) this.deleteAllBtn.style.display = visible ? 'flex' : 'none';
    if (!visible) this.disarmDeleteAll();
  }

  private armDeleteAll(): void {
    if (!this.deleteAllBtn) return;
    this.deleteAllArmed = true;
    this.deleteAllBtn.style.background = 'var(--accent)';
    this.deleteAllBtn.style.color = '#0d1117';
    this.deleteAllBtn.style.borderColor = 'var(--accent)';
    this.deleteAllBtn.title = 'Confirma l\'esborrat';

    this.deleteAllOutsideClickHandler = (e: MouseEvent) => {
      if (e.target !== this.deleteAllBtn) this.disarmDeleteAll();
    };
    document.addEventListener('click', this.deleteAllOutsideClickHandler);
  }

  /** Disarm the delete-all button, resetting its style. Safe to call when nothing is armed. */
  private disarmDeleteAll(): void {
    if (this.deleteAllOutsideClickHandler) {
      document.removeEventListener('click', this.deleteAllOutsideClickHandler);
      this.deleteAllOutsideClickHandler = null;
    }
    if (!this.deleteAllBtn || !this.deleteAllArmed) return;
    this.deleteAllArmed = false;
    this.deleteAllBtn.style.background = 'transparent';
    this.deleteAllBtn.style.color = 'var(--accent)';
    this.deleteAllBtn.style.borderColor = 'var(--accent-alpha-40)';
    this.deleteAllBtn.title = 'Esborra els nodes seleccionats';
  }

  /**
   * Show/hide the [✎] edit button on this screen size — hidden on wide
   * screens, where the side panel already shows the same editable tabs
   * inline, making the popup this button opens redundant. Combines with
   * setEditEnabled()'s selection condition (see there).
   */
  setEditButtonVisible(visible: boolean): void {
    this.editAllowedByScreen = visible;
    this.updateEditButtonDisplay();
  }

  private updateEditButtonDisplay(): void {
    if (this.editBtn) {
      this.editBtn.style.display = (this.editApplicable && this.editAllowedByScreen) ? 'flex' : 'none';
    }
  }

  /**
   * Show/hide the "make main root" (★) button — only applicable to a
   * selected node that is a root of its recipe but not already the main
   * root, same condition the old inline button used.
   */
  setMakeMainRootVisible(visible: boolean): void {
    if (this.makeMainRootBtn) this.makeMainRootBtn.style.display = visible ? 'flex' : 'none';
  }

  destroy(): void {
    this.disarmDeleteAll();
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.createBtn = null;
    this.deleteRecipeBtn = null;
    this.makeMainRootBtn = null;
    this.editBtn = null;
    this.addParentBtn = null;
    this.transformBtn = null;
    this.multiSelectBtn = null;
    this.deleteAllBtn = null;
  }
}
